import { computed, type ComputedRef, type Ref } from 'vue'

import type { ApprovalStage, WorkflowInstance } from '@/types/workflow'
import { getApprovalStageLabel } from '@/utils/workflowPresentation'

import { nextStageLabel } from './workspacePresentation'
import type { StoryboardSceneDraft, UploadedWorkspaceVideo } from './types'

interface ActiveActionLike {
  intent?: 'start' | 'approve' | 'revise' | 'resume' | string
  stage?: ApprovalStage
}

interface BusyStateSnapshot {
  title: string
  description: string
  stageLabel: string
  notice?: string
}

interface SelectOption {
  label: string
  value: string
}

function getFailedApprovalStage(stageName: string): ApprovalStage | null {
  if (stageName === 'proposal_generation') return 'proposal'
  if (stageName === 'script_generation') return 'script'
  if (stageName === 'storyboard_generation') return 'storyboard'
  if (stageName === 'asset_generation' || stageName === 'video_synthesis' || stageName === 'compliance_check' || stageName === 'final_packaging') return 'final'
  return null
}

function getRevisionProcessingStage(stageName: string): ApprovalStage | null {
  if (stageName === 'proposal' || stageName === 'proposal_generation') return 'proposal'
  if (stageName === 'script' || stageName === 'script_generation') return 'script'
  if (stageName === 'storyboard' || stageName === 'storyboard_generation') return 'storyboard'
  if (stageName === 'final' || stageName === 'final_packaging') return 'final'
  return null
}

interface UseWorkspaceStatusOptions {
  loading: Ref<boolean>
  acting: Ref<boolean>
  reviewFeedback: Ref<string>
  workflow: Ref<WorkflowInstance | null>
  workspaceBusyState: Ref<BusyStateSnapshot | null>
  activeAction: Ref<ActiveActionLike | null>
  currentStage: ComputedRef<ApprovalStage>
  currentStageLabel: ComputedRef<string>
  displayStageLabel: ComputedRef<string>
  finalStageFailureStage: ComputedRef<string>
  finalStageVideoUrl: ComputedRef<string>
  effectiveFinalVideoUrl: ComputedRef<string>
  uploadedFinalVideo: ComputedRef<UploadedWorkspaceVideo | null>
  deliveryFinalVideo: ComputedRef<UploadedWorkspaceVideo | null>
  finalVoiceoverMode: Ref<string>
  finalVoiceoverTone: Ref<string>
  finalBgmPlan: Ref<string>
  finalVoiceoverModeOptions: readonly SelectOption[]
  finalVoiceoverToneOptions: readonly SelectOption[]
  finalBgmPlanOptions: readonly SelectOption[]
  storyboardScenes: ComputedRef<StoryboardSceneDraft[]>
  storyboardDraftScenes: Ref<StoryboardSceneDraft[]>
  uploadedSceneVideoMap: ComputedRef<Record<number, UploadedWorkspaceVideo>>
  generatedSceneVideoMap: ComputedRef<Record<number, Record<string, any>>>
  sceneVideoGenerating: Ref<Record<number, boolean>>
  getSceneVideoTaskStatus: (sceneNumber: number) => string
  isSceneVideoTaskActive: (sceneNumber: number) => boolean
}

export function useWorkspaceStatus(options: UseWorkspaceStatusOptions) {
  const finalStageStatusLabel = computed(() => {
    if (options.deliveryFinalVideo.value?.url) {
      return '音频增强成片已生成'
    }
    if (options.uploadedFinalVideo.value?.url) {
      return '人工成片已上传'
    }
    if (options.finalStageVideoUrl.value) {
      return '成片可预览'
    }
    if (options.finalStageFailureStage.value === 'video_synthesis') {
      return '视频生成未完成'
    }
    if (options.finalStageFailureStage.value === 'final_packaging') {
      return '交付打包未完成'
    }
    return '尚未产生成片'
  })

  const finalStageVideoMessage = computed(() => {
    if (options.deliveryFinalVideo.value?.url) {
      return '当前展示的是整合了 TTS 与 BGM 的音频增强成片，可直接下载验收，也可以继续上传新的 BGM 或重新生成旁白。'
    }
    if (options.uploadedFinalVideo.value?.url) {
      return '当前展示的是人工修订后上传的成片，可继续补充配音与修改说明后再确认交付。'
    }
    if (options.finalStageVideoUrl.value) {
      return '当前成片已经生成，可以直接播放预览；如果需要修改内容，请回到上游阶段调整方案、剧本或分镜。'
    }

    const failureError = String(options.workflow.value?.data?.failure_error || '').trim()
    if (options.finalStageFailureStage.value === 'video_synthesis' || options.finalStageFailureStage.value === 'final_packaging') {
      return failureError || '当前还没有生成真正可播放的最终视频。'
    }

    return '当前项目还没有生成真正的成片视频。只有接通可用的视频生成与拼接能力后，这里才会出现播放器。'
  })

  const finalVoiceoverModeLabel = computed(() => options.finalVoiceoverModeOptions.find((item) => item.value === options.finalVoiceoverMode.value)?.label || '未设置')
  const finalVoiceoverToneLabel = computed(() => options.finalVoiceoverToneOptions.find((item) => item.value === options.finalVoiceoverTone.value)?.label || '未设置')
  const finalBgmPlanLabel = computed(() => options.finalBgmPlanOptions.find((item) => item.value === options.finalBgmPlan.value)?.label || '未设置')

  const sceneVideoEntries = computed(() => options.storyboardScenes.value.map((scene) => {
    const sceneNumber = Number(scene.scene_number)
    const uploaded = options.uploadedSceneVideoMap.value[sceneNumber]
    const generated = options.generatedSceneVideoMap.value[sceneNumber]
    const taskStatus = options.getSceneVideoTaskStatus(sceneNumber)
    return {
      scene_number: sceneNumber,
      label: scene.location || `镜头 ${sceneNumber}`,
      video_url: String(uploaded?.url || generated?.video_url || '').trim(),
      sourceLabel: uploaded?.url
        ? '人工上传'
        : (generated?.video_url
          ? (generated?.material_id ? 'AI 生成并归档' : 'AI 生成')
          : (taskStatus === 'queued' || taskStatus === 'running' ? 'AI 生成中' : (taskStatus === 'failed' ? '生成失败' : '待补充'))),
    }
  }))

  const finalStageFacts = computed(() => ([
    { label: '成片状态', value: finalStageStatusLabel.value },
    { label: '成片来源', value: options.deliveryFinalVideo.value?.url ? '音频增强版' : (options.uploadedFinalVideo.value?.url ? '人工终版' : '镜头自动拼接') },
    { label: '配音策略', value: finalVoiceoverModeLabel.value },
    { label: '镜头覆盖', value: `${sceneVideoEntries.value.filter((item) => item.video_url).length}/${options.storyboardScenes.value.length || 0}` },
  ]))

  const pendingApprovalLabel = computed(() => {
    if (options.acting.value && options.activeAction.value?.intent === 'approve' && options.activeAction.value.stage) {
      return getApprovalStageLabel(nextStageLabel(options.activeAction.value.stage))
    }
    if (options.acting.value && options.activeAction.value?.intent === 'revise' && options.activeAction.value.stage) {
      return getApprovalStageLabel(options.activeAction.value.stage)
    }
    return options.workflow.value?.pending_approval?.stage ? getApprovalStageLabel(options.workflow.value.pending_approval.stage) : '暂无'
  })

  const workflowBusySnapshot = computed<BusyStateSnapshot | null>(() => {
    if (!options.workflow.value || options.acting.value || options.workspaceBusyState.value) {
      return null
    }

    const workflowState = String(options.workflow.value.state || '').trim()
    const pendingStage = options.workflow.value.pending_approval?.stage || null
    const revisionStage = getRevisionProcessingStage(
      String(
        options.workflow.value.data?.revision_context?.stage
        || options.workflow.value.data?.revision_context?.source_stage
        || pendingStage
        || options.currentStage.value
      ).trim()
    ) || options.currentStage.value

    if (options.loading.value) {
      return {
        title: '系统正在同步项目工作台',
        description: '正在拉取项目、工作流和分镜数据，完成后会自动显示最新状态。',
        stageLabel: options.displayStageLabel.value,
        notice: '首次进入或切换阶段时会短暂同步一次，你不需要手动刷新页面。',
      }
    }

    if (workflowState === 'revision_in_progress') {
      return {
        title: `AI 正在重新生成${getApprovalStageLabel(revisionStage)}`,
        description: options.workflow.value.data?.current_focus || `系统正在根据你的修改意见重新生成${getApprovalStageLabel(revisionStage)}，完成后会自动刷新当前页面。`,
        stageLabel: getApprovalStageLabel(revisionStage),
        notice: '这是后台长任务，页面会自动轮询最新结果。生成期间正文可能暂时保持旧版本，直到新结果真正落库。',
      }
    }

    if (pendingStage) {
      return null
    }

    if (!workflowState || ['draft', 'completed', 'delivered', 'archived', 'cancelled', 'failed', 'on_hold'].includes(workflowState)) {
      return null
    }

    const stageCopy: Record<ApprovalStage, { title: string; description: string }> = {
      proposal: {
        title: 'AI 正在启动需求分析与方案草拟',
        description: '系统正在梳理需求、目标用户和创意方向，并生成第一版方案确认内容。',
      },
      script: {
        title: 'AI 正在生成剧本首稿',
        description: '系统正在基于确认后的方案生成完整剧本，并吸收当前项目的素材线索与风格要求。',
      },
      storyboard: {
        title: 'AI 正在拆解分镜与镜头结构',
        description: '系统正在将剧本转换成可编辑的镜头卡片，并整理画面、动作、镜头和节奏信息。',
      },
      final: {
        title: 'AI 正在生成成片并整理交付结果',
        description: '系统正在汇总镜头视频、拼接成片并整理最终交付信息，完成后会直接进入成片验收页。',
      },
    }

    const stage = options.currentStage.value
    return {
      title: stageCopy[stage].title,
      description: options.workflow.value.data?.current_focus || stageCopy[stage].description,
      stageLabel: options.currentStageLabel.value,
      notice: '页面会自动刷新当前阶段结果，你可以先继续整理文案、参考素材和交付要求。',
    }
  })

  const activeActionTitle = computed(() => {
    if (options.loading.value) return '系统正在同步项目工作台'
    if (options.workspaceBusyState.value?.title) return options.workspaceBusyState.value.title
    if (options.acting.value && options.activeAction.value?.intent === 'start') {
      return options.activeAction.value.stage === 'script' ? '系统正在载入导入剧本' : 'AI 正在启动需求分析与方案草拟'
    }
    if (options.acting.value && options.activeAction.value?.intent === 'approve') {
      return `AI 正在根据你的确认推进${nextStageLabel(options.activeAction.value.stage)}`
    }
    if (options.acting.value && options.activeAction.value?.intent === 'revise') {
      return `AI 正在根据你的意见重做${getApprovalStageLabel(options.activeAction.value.stage || options.currentStage.value)}`
    }
    if (options.acting.value && options.activeAction.value?.intent === 'resume') return 'AI 正在恢复工作流并继续生成'
    if (options.workflow.value?.pending_approval?.stage === options.currentStage.value) return `${options.currentStageLabel.value}已生成，等待你确认`
    return '项目工作台已就绪'
  })

  const activeActionDescription = computed(() => {
    if (options.activeAction.value?.intent === 'start') {
      return options.activeAction.value.stage === 'script'
        ? '系统正在把导入剧本挂到剧本审批节点，完成后会直接停在当前工作台。'
        : '系统正在整理需求并生成第一版结果。'
    }
    if (options.activeAction.value?.intent === 'approve') return '系统正在继续下一阶段，完成后会自动把新结果带回当前页面。'
    if (options.activeAction.value?.intent === 'revise') return '系统正在根据你的修改意见重做当前阶段内容。'
    if (options.activeAction.value?.intent === 'resume') return '系统正在从中断处继续处理。'
    return '你可以在这里直接查看结果、编辑内容并提交确认。'
  })

  const currentFocusText = computed(() => {
    if (options.loading.value) {
      return '正在同步项目、工作流和工作台数据。'
    }
    if (options.workspaceBusyState.value?.description) {
      return options.workspaceBusyState.value.description
    }
    if (workflowBusySnapshot.value?.description) {
      return workflowBusySnapshot.value.description
    }
    if (options.acting.value) {
      return activeActionDescription.value
    }
    return options.workflow.value?.data?.current_focus || activeActionDescription.value
  })

  const pageSubtitle = computed(() => {
    if (options.loading.value) {
      return '系统正在同步最新项目状态，完成后会自动刷新当前页面。'
    }
    if (options.workspaceBusyState.value || workflowBusySnapshot.value || options.acting.value) {
      return `系统正在处理${workspaceBusyStageLabel.value}，完成后会自动刷新到当前页面。`
    }
    if (options.workflow.value?.pending_approval?.stage === options.currentStage.value) {
      return `当前已经进入${options.currentStageLabel.value}，你可以直接在此页修改结果并确认继续。`
    }
    if (options.workflow.value?.state === 'completed' || options.workflow.value?.state === 'delivered') {
      return '当前项目已经完成，你仍然可以在这里回看方案、剧本、分镜和交付结果。'
    }
    return '这里统一承接生成、查看、修改和确认，不再拆成流程页和工作台两个入口。'
  })

  const sideGuidance = computed(() => {
    const state = options.workflow.value?.state || 'draft'
    const pendingStage = options.workflow.value?.pending_approval?.stage || null
    const failureError = String(options.workflow.value?.data?.failure_error || '').trim()
    const failedApprovalStage = getFailedApprovalStage(String(options.workflow.value?.data?.last_failed_stage || '').trim())
    if (state === 'draft') return '工作流尚未启动。'
    if (state === 'revision_in_progress') {
      return options.workflow.value?.data?.current_focus || '系统正在根据你的修改意见重新生成当前阶段内容，完成后会自动刷新当前页面。'
    }
    if (failureError && failedApprovalStage && failedApprovalStage === (pendingStage || options.currentStage.value)) {
      return `${getApprovalStageLabel(failedApprovalStage)}重新生成失败：${failureError}。你可以继续编辑当前结果并再次提交。`
    }
    if (pendingStage === options.currentStage.value) {
      return `当前${options.currentStageLabel.value}已经生成完成，请确认后继续进入下一阶段。`
    }
    if (pendingStage) {
      return `当前待处理的是${getApprovalStageLabel(pendingStage)}，你也可以继续查看这一阶段的已有结果。`
    }
    if (options.finalStageFailureStage.value === 'video_synthesis') return finalStageVideoMessage.value
    if (options.finalStageFailureStage.value === 'final_packaging') return finalStageVideoMessage.value
    if (state === 'failed') return '当前流程执行失败，可在这里直接恢复并继续推进。'
    if (state === 'on_hold') return '当前流程处于暂停状态，可在这里恢复。'
    if (state === 'completed' || state === 'delivered') return '当前项目已完成，可直接点击右侧阶段卡片，回看方案、剧本、分镜和最终交付内容。'
    return options.workflow.value?.data?.current_focus || '当前阶段状态已同步，你可以继续查看、修改和确认结果。'
  })

  const approveButtonLabel = computed(() => {
    if (options.currentStage.value === 'final') {
      return '确认完成'
    }
    return `确认并继续${nextStageLabel(options.currentStage.value)}`
  })

  const storyboardMissingImageSceneNumbers = computed(() => options.storyboardDraftScenes.value
    .filter((scene) => !String(scene.background_image_url || '').trim())
    .map((scene) => Number(scene.scene_number)))

  const storyboardMissingVideoSceneNumbers = computed(() => options.storyboardDraftScenes.value
    .filter((scene) => {
      const sceneNumber = Number(scene.scene_number)
      return !String(options.uploadedSceneVideoMap.value[sceneNumber]?.url || options.generatedSceneVideoMap.value[sceneNumber]?.video_url || '').trim()
    })
    .map((scene) => Number(scene.scene_number)))

  const storyboardSceneVideoGenerating = computed(() => (
    Object.values(options.sceneVideoGenerating.value).some(Boolean)
    || options.storyboardDraftScenes.value.some((scene) => options.isSceneVideoTaskActive(Number(scene.scene_number)))
  ))

  const storyboardAssetsReady = computed(() => (
    Boolean(options.storyboardDraftScenes.value.length)
    && storyboardMissingImageSceneNumbers.value.length === 0
    && storyboardMissingVideoSceneNumbers.value.length === 0
    && !storyboardSceneVideoGenerating.value
  ))

  const storyboardApprovalHint = computed(() => {
    if (!options.storyboardDraftScenes.value.length) {
      return '当前还没有可确认的分镜镜头。'
    }
    if (storyboardSceneVideoGenerating.value) {
      return '镜头视频仍在生成中，生成完成后才能继续进入成品制作。'
    }
    if (storyboardMissingImageSceneNumbers.value.length || storyboardMissingVideoSceneNumbers.value.length) {
      const parts: string[] = []
      if (storyboardMissingImageSceneNumbers.value.length) {
        parts.push(`缺图片镜头：${storyboardMissingImageSceneNumbers.value.join('、')}`)
      }
      if (storyboardMissingVideoSceneNumbers.value.length) {
        parts.push(`缺视频镜头：${storyboardMissingVideoSceneNumbers.value.join('、')}`)
      }
      return `所有镜头都确认完成后，继续进入成品制作页面进行整片整合与配音确认。当前仍有未完成项，${parts.join('；')}。`
    }
    return '所有镜头都确认完成后，继续进入成品制作页面进行整片整合与配音确认。'
  })

  const canApproveCurrentStage = computed(() => {
    if (options.acting.value || !options.workflow.value?.pending_approval) {
      return false
    }
    if (options.workflow.value.state === 'on_hold' || options.workflow.value.state === 'failed') {
      return false
    }
    if (options.currentStage.value === 'storyboard') {
      return storyboardAssetsReady.value
    }
    return true
  })

  const canRequestRevision = computed(() => {
    if (options.acting.value) {
      return false
    }
    return Boolean(options.reviewFeedback.value.trim())
  })

  const stateTagType = computed(() => {
    const state = options.workflow.value?.state || 'draft'
    if (state.includes('waiting') || state === 'pending_confirm') return 'warning'
    if (state === 'failed') return 'danger'
    if (state === 'completed' || state === 'delivered') return 'success'
    return 'info'
  })

  const isWorkspaceBusy = computed(() => (
    options.loading.value
    || options.acting.value
    || Boolean(options.workspaceBusyState.value)
    || Boolean(workflowBusySnapshot.value)
  ))
  const workspaceBusyTitle = computed(() => options.workspaceBusyState.value?.title || workflowBusySnapshot.value?.title || activeActionTitle.value)
  const workspaceBusyDescription = computed(() => options.workspaceBusyState.value?.description || workflowBusySnapshot.value?.description || activeActionDescription.value)
  const workspaceBusyStageLabel = computed(() => options.workspaceBusyState.value?.stageLabel || workflowBusySnapshot.value?.stageLabel || options.displayStageLabel.value)
  const workspaceBusyNotice = computed(() => options.workspaceBusyState.value?.notice || workflowBusySnapshot.value?.notice || '页面会自动刷新结果。你可以先继续整理当前文案、配图和素材线索，不需要额外盯流程记录。')

  return {
    activeActionDescription,
    activeActionTitle,
    approveButtonLabel,
    canApproveCurrentStage,
    canRequestRevision,
    currentFocusText,
    finalStageFacts,
    finalStageStatusLabel,
    finalStageVideoMessage,
    isWorkspaceBusy,
    pageSubtitle,
    pendingApprovalLabel,
    sideGuidance,
    storyboardApprovalHint,
    storyboardAssetsReady,
    storyboardMissingImageSceneNumbers,
    storyboardMissingVideoSceneNumbers,
    storyboardSceneVideoGenerating,
    stateTagType,
    workflowBusySnapshot,
    workspaceBusyDescription,
    workspaceBusyNotice,
    workspaceBusyStageLabel,
    workspaceBusyTitle,
  }
}